import React, { useEffect, useState } from "react";
import { ArrowUpRight, Newspaper } from "lucide-react";
import { getSports, getNews } from "../lib/api";

const ALL = { id: "all", label: "All" };

const News = () => {
  const [sports, setSports] = useState([]);
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);

  useEffect(() => {
    getSports().then(setSports).catch(() => setSports([]));
  }, []);

  useEffect(() => {
    getNews(filter === "all" ? null : filter)
      .then((data) => {
        setItems(data);
      })
      .catch(() => setItems([]));
  }, [filter]);

  const chips = [ALL, ...sports.map((s) => ({ id: s.id, label: s.label }))];
  const imageBySport = sports.reduce((acc, s) => {
    acc[s.id] = s.image;
    return acc;
  }, {});

  return (
    <section id="news" className="py-16 sm:py-24 border-t border-[var(--line)]">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-4 mb-5">
              <span className="h-[2px] w-10 bg-[var(--olive)]" />
              <span className="text-xs font-bold tracking-eyebrow text-[var(--muted)] uppercase">
                From around the country
              </span>
            </div>
            <h2 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-[var(--ink)]">
              Latest{" "}
              <span className="font-serif-display italic font-medium text-[var(--olive-2)]">
                news
              </span>
            </h2>
          </div>
          <p className="text-[var(--muted)] max-w-sm text-[15px] leading-relaxed">
            Curated coverage from outlets like the Baltimore Sun, USA Lacrosse,
            United Soccer Coaches and MaxPreps. Filter by sport.
          </p>
        </div>

        {/* filter chips */}
        <div className="flex flex-wrap gap-2.5 mb-8">
          {chips.map((c) => (
            <button
              key={c.id}
              data-testid={`news-filter-${c.id}`}
              onClick={() => setFilter(c.id)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                filter === c.id
                  ? "bg-[var(--ink)] text-[var(--cream)]"
                  : "bg-[var(--paper)] text-[var(--ink)] border border-[var(--line)] hover:border-[var(--olive)]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {items.map((n) => (
            <a
              key={n.id}
              href={n.source_url}
              target={n.source_url.startsWith("#") ? undefined : "_blank"}
              rel="noopener noreferrer"
              className="group flex flex-col overflow-hidden rounded-3xl border border-[var(--line)] bg-[var(--paper)] transition-all hover:-translate-y-1 hover:border-[var(--olive)] hover:shadow-[0_16px_32px_-24px_rgba(30,58,44,0.4)]"
            >
              {(n.image || imageBySport[n.sport]) && (
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={n.image || imageBySport[n.sport]}
                    alt={n.sport_label}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute top-4 left-4 rounded-full bg-[var(--ink)]/90 backdrop-blur px-3 py-1.5 text-[11px] font-bold tracking-wide uppercase text-[var(--cream)]">
                    {n.sport_label}
                    {n.division_label ? ` · ${n.division_label}` : ""}
                  </span>
                </div>
              )}
              <div className="flex flex-col justify-between flex-1 p-6">
                <div>
                  <span className="text-[12px] font-semibold text-[var(--muted)]">
                    {n.date}
                  </span>
                  <h3 className="mt-2 text-xl font-extrabold leading-snug text-[var(--ink)] group-hover:text-[var(--olive-2)] transition-colors">
                    {n.title}
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-[var(--muted)]">
                    {n.excerpt}
                  </p>
                </div>
                <div className="mt-5 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[var(--ink)]">
                    <Newspaper className="w-4 h-4 text-[var(--olive-2)]" />
                    {n.source}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-[var(--muted)] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </a>
          ))}
          {items.length === 0 && (
            <p className="text-[var(--muted)] text-sm py-10">
              No stories for this sport yet.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default News;
